<template>
    <div class="animated fadeIn">
        <b-card>
            <b-tabs pills v-model="tabIndex" small>
                <b-tab :title="$t('explore')" @click="tabChange('explore')">
                    <template v-if="templates">
                        <b-row>
                            <b-col xs="12" md="6" lg="6">
                                <b-form-group>
                                    <b-form-radio-group>
                                        <b-form-radio v-model="searchBy" name="searchBy" value="name">{{ $t('nameTag')
                                            }}</b-form-radio>
                                        <b-form-radio v-model="searchBy" name="searchBy" value="utid">{{ $t('utid')
                                            }}</b-form-radio>
                                    </b-form-radio-group>
                                </b-form-group>
                                <b-row>
                                    <b-col>
                                        <b-input type="search" v-model="keyword" :placeholder="`${$t('keyword')}`" />
                                    </b-col>
                                    <b-col>
                                        <b-button variant="dark" @click="activationCodeModal = true"> {{
                                            $t('iHaveActivationCode') }} </b-button>
                                        <main-dialog v-model="activationCodeModal" ok-only :ok-title="`${$t('activate')}`"
                                            hide-header :hide-footer="!activationTemplate">
                                            <b-row>
                                                <b-col sm="11"><b-input v-model="activationCode"
                                                        :placeholder="`${$t('activationCode')}`"
                                                        @keyup="searchTemplateByActivationCode"
                                                        :disabled="activationCodeSearching" /></b-col>
                                                <b-col sm="1"><b-spinner v-if="activationCodeSearching" small /></b-col>
                                            </b-row>
                                        </main-dialog>
                                    </b-col>
                                </b-row>
                            </b-col>
                        </b-row>

                        <br />

                        <b-spinner v-if="searching" />

                        <b-row>
                            <template v-if="templates">
                                <b-col lg="3" md="6" sm="12" v-for="temp in templates" :key="temp.id"
                                    @click="select(temp.id)">
                                    <b-card :class="{ selected: isSelected(temp.id) }">
                                        <img :src="temp.cover" width="100%" />
                                        <b-card-footer>{{ $translateLabel(temp.label) }}
                                            <b-badge v-if="temp.upgrade_from !== null" variant="warning"> {{
                                                $t('templateIsUpgrade') }} </b-badge>
                                            <b-badge v-if="temp.upgrade_to !== null" variant="warning"> {{
                                                $t('templateIsUpgradable') }} </b-badge>

                                        </b-card-footer>
                                    </b-card>
                                </b-col>
                            </template>
                            <div class="text-center" v-else>
                                <b-spinner />
                            </div>
                        </b-row>
                    </template>
                    <b-spinner v-else />
                </b-tab>
                <b-tab :disabled="templatesAvailable.length < 1" @click="tabChange('available')">
                    <template #title>
                        {{ $t('available') }} <b-badge>{{ templatesAvailable.length }}</b-badge>
                    </template>
                    <b-row>
                        <b-col lg="3" md="6" sm="12" v-for="temp in templatesAvailable" :key="temp.id"
                            @click="selectAvailable(temp.id)">
                            <b-card :class="{ selected: isSelected(temp.id) }" class="template">
                                <img :src="temp.cover" width="80%" />
                                <b-card-footer>
                                    {{ $translateLabel(temp.label) }}
                                    <br />
                                    <b-badge variant="success" v-if="isInstalled(temp)">{{ $t('installed') }}</b-badge>
                                    <b-badge variant="warning" v-if="isTrial(temp)" class="ml-2">{{ $t('trial')
                                        }}</b-badge>

                                    <b-badge v-if="!temp.price" > {{ $t('free') }} </b-badge>
                                    <b-badge v-else variant="success"> {{ $t('owned') }} </b-badge>
                                    <b-badge v-if="temp.upgrade_from !== null" variant="warning"> {{
                                        $t('templateIsUpgrade') }} </b-badge>
                                    <b-badge v-if="temp.upgrade_to !== null" variant="warning"> {{
                                        $t('templateIsUpgradable') }} </b-badge>
                                </b-card-footer>
                            </b-card>
                        </b-col>
                    </b-row>
                </b-tab>
            </b-tabs>
        </b-card>


        <main-dialog v-model="processInfo" hide-header hide-footer @hide="(e) => e.preventDefault()">
            <div class="text-center">
                <b-spinner /> <br />
                {{ processName }}
            </div>
        </main-dialog>

        <main-dialog v-model="showUserInputModal" hide-header @ok="install" :ok-title="$t('install')" size="lg">
            <user-input-template :components="selected.components" :integrations="selected.integrations"
                :apps="selected.apps" :functions="selected.functions" v-if="selected" />
        </main-dialog>

        <b-sidebar id="template" :title="$translateLabel(selected.label)" v-if="selected !== null" right
            v-model="visible" no-header backdrop style="padding-top:60px">

            <div class="px-3 py-2 mt-5">

                <template v-if="loadingTemplate">
                    <b-skeleton width="100%" height="1024px" animation="throb" />
                </template>

                <Description :item="selectedTemplate" type="single" @upgradeClicked="(template) => select(template.id)" v-else></Description>

            </div>

            <template #footer v-if="!loadingTemplate">
                <div class="d-flex bg-dark text-light align-items-center px-3 py-2">
                    <b-button size="sm" variant="primary" @click="attemptInstall" v-if="canShowInstall">{{ $t('install')
                        }}</b-button>

                    <protokol-checkout-button size="sm" class="ml-2" v-if="canShowBuy" productType="template"
                        :reference="selected.id" :source="`template:${selected.id}`" />
                    <b-button size="sm" class="ml-2" @click="startTrial" v-if="canShowTrial">{{ $t('startTrial')
                        }}</b-button>
                    <b-button size="sm" variant="warning" class="ml-2" @click="uninstall(selected.id)"
                        v-if="canShowUninstall">{{ $t('uninstall') }}</b-button>
                    
                </div>
            </template>
        </b-sidebar>

        <main-dialog v-model="showBillingInformationModal" hide-cancel hide-ok>
            <BillingInfo @saved="() => this.billingInfoSaved = true" v-if="!billingInfoSaved" />
            <p v-else> {{ $t('yourBillingInfoIsSaved') }} </p>

            <template #modal-footer>
                <b-button variant="success" @click="buy" :disabled="!billingInfoSaved"> {{ $t('goToCheckout') }}
                </b-button>
            </template>
        </main-dialog>
    </div>
</template>

<style scoped lang="scss">
@import '~bootstrap/scss/bootstrap';

.template-header {
    text-align: left;
}

.selected {
    border: 1px solid $success;
}

.card-body {
    padding: 0px !important;
}

.template {
    text-align: center;

    & img {
        padding: 10px;
    }
}
</style>

<script>
import debounce from 'lodash/debounce'
import truncate from 'lodash/truncate'
import Template from '../../lib/Template'
import Vue from 'vue'
import safeEval from 'safe-eval'
import ErrorPage from '../pages/Page500'
import Description from './Description'
import BillingInfo from '@/views/users/BillingInfo'
import Price from '@/plugins/protokol/lib/Price'

export default {
    name: "Templates",
    components: {
        ErrorPage,
        Description,
        BillingInfo
    },
    props: {},
    data: function () {
        return {
            keyword: "",
            templates: null,
            error: false,
            selected: null,
            visible: false,
            processInfo: false,
            processName: '',
            searching: false,
            showUserInputModal: false,
            userInputComponent: null,
            activationCode: null,
            activationCodeModal: false,
            activationTemplate: null,
            activationCodeSearching: false,
            templatesAvailable: [],
            selectedTemplate: null,
            upgradeAvailable: null,
            upgradeTemplate: null,
            workspaceTemplate: null,
            searchBy: 'name',
            pageUnavailable: false,
            showBillingInformationModal: false,
            billingInfoSaved: false,
            loadingTemplate: true,
            projectTemplates: [],
            tabIndex: 0,
            tabHash: ["#explore", "#available"],
        }
    },
    watch: {
        keyword: debounce(function (nVal) {
            this.search(nVal)
        }, 500),

        $route() {
            let hash = window.location.hash
            this.tabIndex = this.tabHash.indexOf(hash)
        }
    },

    methods: {
        search(keyword) {
            this.searching = true;

            this.$http.post("/v1/project/template/search", { keyword: keyword, type: this.searchBy }).then(resp => {
                this.templates = resp.data || []
                this.templates = this.templates.filter(t => {
                    let available = this.templatesAvailable.find(ta => ta.id == t.id || t.upgrade_to == ta.id)

                    return available === undefined
                })
            }).catch(err => {
                this.error = true
            }).finally(() => {
                this.searching = false
            })
        },

        attemptInstall() {
            if (this.selected.require_user_input) {
                let m = safeEval('(function() {' + this.selected.user_input.script + '}());')
                if (m && m.stack && m.message) {
                    this.selected.user_input_template = "<b-alert :show='true'> Unexpected error happened! </b-alert>"
                }

                Vue.component('user-input-template', {
                    template: "<div>" + this.selected.user_input.template + "</div>",
                    props: ["components", "functions", "apps", "integrations", "workflows", "resources"],
                    ...m
                })

                this.showUserInputModal = true
                return
            }

            this.install()
        },

        attemptUpgrade() {
            if (this.selected.require_user_input) {
                let m = safeEval('(function() {' + this.selected.user_input.script + '}());')
                if (m && m.stack && m.message) {
                    this.selected.user_input_template = "<b-alert :show='true'> Unexpected error happened! </b-alert>"
                }

                Vue.component('user-input-template', {
                    template: "<div>" + this.selected.user_input.template + "</div>",
                    props: ["components", "functions", "apps", "integrations", "workflows", "resources"],
                    ...m
                })

                this.showUserInputModal = true
                return
            }

            this.upgrade()
        },

        install() {
            this.visible = false
            this.$bvToast.toast("Sit tight while we install the template. This can take a few moments.", {
                id: 'template-install',
                variant: 'info',
                title: 'Installing...',
                autoHideDelay: 30000,
                appendToast: false
            })

            const templateToInstall = _.cloneDeep(this.selected)

            this.$http.post("/v1/project/template/install", {
                id: this.selected.id,
                activationCode: this.activationCode,
                data: {
                    components: templateToInstall.components,
                    integrations: templateToInstall.integrations,
                    apps: templateToInstall.apps,
                    functions: templateToInstall.functions
                }
            }).then(resp => {
                if (resp) {
                    this.$store.commit('RELOAD_CONTAINER')
                } else {
                    this.$bvToast.toast("Your template is setting up. It will be available soon.", {
                        id: 'template-install',
                        variant: 'info',
                        title: 'Setting up...',
                        autoHideDelay: 30000,
                        appendToast: false
                    })
                }
            }).catch(err => {
                this.$mainDialog.msgBoxOk(this.$t('failedToInstallTemplate'))
            }).finally(() => {
                this.$bvToast.hide('template-install')
            })
        },

        buy() {
            var tmp = new Template(
                this.selected,
                this.$store.getters.getLocale,
                this.activationCode
            )

            let billing = this.$store.getters.getUser.settings.billing_address || {}
            if (!billing.country || billing.country === null || billing.country == "") {
                this.showBillingInformationModal = true
                return
            }

            const popup = this.$loadingPopup()

            this.$gtag.event('template_buy', { productType: 'template', product: tmp })

            this.$transactionApi.post("/checkout/link", {
                product_type: "template",
                reference: this.selected.id,
                lang: this.$store.getters.getLocale,
                source: `template:${this.selected.id}`
            }).then(resp => {
                window.open(resp.data, '_blank')
            }).catch(err => {
                this.processInfo = false

                if (err.response) {
                    if (err.response.status == 501) {
                        this.$bvToast.toast(this.$t('checkoutMethodCurrentlyDisabled'), {
                            title: this.$t('error'),
                            variant: 'danger'
                        })
                        return
                    }
                }

                this.$mainDialog.msgBoxOk(this.$t('failedToCheckout'))
            }).finally(() => {
                popup.hide()
            })
        },

        upgrade() {
            var tmp = new Template(
                this.selected,
                this.$store.getters.getLocale,
                this.activationCode
            )

            let billing = this.$store.getters.getUser.settings.billing_address || {}
            if (!billing.country || billing.country === null || billing.country == "") {
                this.showBillingInformationModal = true
                return
            }

            const popup = this.$loadingPopup()

            this.$gtag.event('template_buy', { productType: 'template', product: tmp })

            this.$transactionApi.post("/checkout/link", {
                product_type: "template",
                reference: this.selected.id,
                lang: this.$store.getters.getLocale,
                source: `template:${this.selected.id}`
            }).then(resp => {
                window.open(resp.data, '_blank')
            }).catch(err => {
                this.processInfo = false

                this.$mainDialog.msgBoxOk(this.$t('failedToCheckout'))
            }).finally(() => {
                popup.hide()
            })
        },

        buyTemplate(template) {
            var tmp = new Template(
                template,
                this.$store.getters.getLocale,
                this.activationCode
            )

            let billing = this.$store.getters.getUser.settings.billing_address || {}
            if (!billing.country || billing.country === null || billing.country == "") {
                this.showBillingInformationModal = true
                return
            }

            const popup = this.$loadingPopup()

            this.$gtag.event('template_buy', { productType: 'template', product: tmp })

            this.$transactionApi.post("/checkout/link", { product_type: "template", reference: tmp.template.id, lang: this.$store.getters.getLocale }).then(resp => {
                window.open(resp.data, '_blank')
            }).catch(err => {
                this.processInfo = false

                this.$mainDialog.msgBoxOk(this.$t('failedToCheckout'))
            }).finally(() => {
                popup.hide()
            })
        },

        startTrial() {
            const popup = this.$loadingPopup()
            this.$transactions.startTrial({
                product_type: "template",
                reference: this.selected.id,
                source: "template"
            }).then((resp) => {
                this.$bvToast.toast(this.$t('trialIsNowActive'), {
                    variant: 'success',
                    title: this.$t('success')
                })

                resp.data.forEach(d => {
                    this.$store.commit("ADD_TRIAL", d)
                })

                this.$store.dispatch("fetchSubscriptions", true)
                this.getAvailableTemplates()
            }).catch((err) => {
                this.$debugLog()
                this.$bvToast.toast(this.$t('failedToStartTrial'), {
                    variant: 'danger',
                    title: this.$t('error')
                })
            }).finally(() => {
                popup.hide()
            })
        },

        uninstall(id) {
            this.$mainDialog.msgBoxConfirm(this.$t('removedData')).then(resp => {
                if (resp) {
                    const popup = this.$loadingPopup(this.$t('uninstallingTemplate'))

                    this.$http.post("/v1/project/template/uninstall", { id: id }).then(() => {
                        this.$store.commit('RELOAD_CONTAINER')
                        //this.$router.go()
                    }).catch(err => {
                        this.$debugLog(err)

                        this.$bvToast.toast(this.$t('failedToUninstallTemplate'), {
                            variant: 'danger',
                            title: this.$t('error')
                        })
                    }).finally(() => {
                        popup.hide()
                    })
                }
            })
        },

        select(id) {
            this.visible = true
            this.loadingTemplate = true

            this.$http.get(`/v1/project/template/${id}`).then(resp => {
                this.selected = resp.data
                this.selectedTemplate = new Template(this.selected, this.$store.getters.getLocale)

                this.visible = true
            }).catch(err => {
                this.$debugError(err)
                this.visible = false
            }).finally(() => {
                this.loadingTemplate = false
            })
        },

        selectAvailable(id) {
            this.visible = true
            this.loadingTemplate = true

            this.$http.get(`/v1/project/template/${id}`).then(resp => {
                this.selected = resp.data
                this.selectedTemplate = new Template(this.selected, this.$store.getters.getLocale)

                this.visible = true
            }).catch(err => {
                this.$debugError(err)
                this.visible = false
            }).finally(() => {
                this.loadingTemplate = false
            })
        },

        selectUpgrade(id) {
            this.visible = true
            this.loadingTemplate = true

            this.$http.get(`/v1/project/template/${id}`).then(resp => {
                this.selected = resp.data
                this.selectedTemplate = new Template(this.selected, this.$store.getters.getLocale)

                this.visible = true
            }).catch(err => {
                this.$debugError(err)
                this.visible = false
            }).finally(() => {
                this.loadingTemplate = false
            })
        },

        truncateName(name) {
            return truncate(name, { length: 18 })
        },

        isSelected(id) {
            if (this.selected) {
                return this.selected.id == id
            }

            return false
        },

        searchTemplateByActivationCode: debounce(function () {
            this.activationCodeSearching = true
            this.$http.get("/v1/project/template/activationCode/" + this.activationCode).then((resp) => {
                this.templates = [resp.data]
                this.activationCodeModal = false
                this.selected = resp.data
                this.visible = true
                this.selectedTemplate = new Template(this.selected, this.$store.getters.getLocale)
            }).catch(err => {

                this.$bvToast.toast(this.$t('invalidActivationCode'), {
                    variant: 'danger',
                    title: "Error"
                })
            }).finally(() => {
                this.activationCodeSearching = false
            })
        }, 1000),

        canShowInstallForUpgrade(upgradeTemplate) {
            let valid = this.$store.getters.getSubscriptions.find(s => {
                return s.product_id == upgradeTemplate.product_id
            })

            return this.getPriceAndCurrency(upgradeTemplate).price <= 0 || valid !== undefined
        },

        getAvailableTemplates() {
            this.$http.get("/v1/project/template/available").then(resp => {
                this.templatesAvailable = resp.data || []
                this.templatesAvailable = this.templatesAvailable.filter(ta => {
                    if (ta.upgrade_to_template) {
                        let upgrade = ta.upgrade_to_template.id
                        let foundUpgrade = this.templatesAvailable.find(a => upgrade == a.id)

                        return foundUpgrade === undefined
                    }

                    return true
                })

                this.search("")
            }).catch((err) => {
                this.workspaceTemplate = null
                this.templatesAvailable = []
                this.$pageUnavailable()
            })
        },

        canShowBuyForTemplate(template) {
            let valid = this.$store.getters.getSubscriptions.find(s => {
                return s.product_id == template.product_id
            })

            if (valid === undefined) {
                return true
            }

            return valid.is_trial
        },

        priceForTemplate(template) {
            return this.getPriceAndCurrency(template)
        },

        getPriceAndCurrency(product) {
            let { country = 'en_US' } = this.$store.getters.getUser.settings.billing_address || {}

            const price = new Price(
                country,
                this.$store.getters.getLocale
            )

            const currency = price.getCurrency()

            var tmp = new Template(
                product,
                this.$store.getters.getLocale,
                this.activationCode
            )

            return {
                price: tmp.priceInCurrency(currency),
                currency: currency
            }
        },

        isInstalled(template) {
            return this.projectTemplates.find(temp => {
                return temp.template.id == template.id
            })
        },

        isTrial(template) {
            return this.trials.find(trial => {
                return trial.product_id == template.product_id
            })
        },

        tabChange(e) {
            window.location.hash = e
        },
    },

    computed: {
        workspace() {
            return this.$store.getters.getCurrentWorkspace
        },

        upgradeAlreadyAvailable() {

        },

        canShowInstall() {
            if (this.isInstalled(this.selected)) return false

            let valid = this.$store.getters.getSubscriptions.find(s => {
                return s.product_id == this.selected.product_id
            })

            return this.price.price <= 0 || valid !== undefined
        },

        canShowUninstall() {
            return this.isInstalled(this.selected)
        },

        canShowTrial() {
            if (this.canShowInstall) {
                return false
            }

            if (this.$store.getters.isTrialUsed(this.selected.product_id)) {
                return false
            }

            return this.selected.trial_period !== null
        },

        hasUpgrade() {
            return this.selected.upgrade_to != null
        },

        canShowBuy() {
            if (this.price.price <= 0) {
                return false
            }

            let valid = this.$store.getters.getSubscriptions.find(s => {
                return s.product_id == this.selected.product_id
            })

            console.log("canShowBuy", valid)

            if (valid === undefined) {
                return true
            }

            return valid.is_trial
        },

        price() {
            return this.getPriceAndCurrency(this.selected)
        },

        trials() {
            return this.$store.getters.getTrials
        }
    },

    created() {
        let hash = window.location.hash
        this.tabIndex = this.tabHash.indexOf(hash)
    },

    async mounted() {
        let { data = [] } = await this.$http.get("/v1/project/template/workspace")
        this.projectTemplates = data || []

        this.getAvailableTemplates()

        let checkoutPage = new BroadcastChannel('checkout')
        checkoutPage.onmessage = (msg) => {
            if (msg.data.type == "checkout_finished") {
                this.$store.dispatch("fetchSubscriptions", true)
            }
        }

        this.$store.dispatch("fetchTrials")
        this.$store.dispatch("fetchSubscriptions")

        this.$store.dispatch("initTutorial", "templates")
    }
}
</script>
